// The table's scenes (issue 834; CAMP-004, CAMP-005): the DM's. Each scene is
// its own map with everything on it. "View" takes the DM's own screen there,
// where nobody else can see (staging); "Go live" moves every player to it at
// once. The first scene holds the roll log and stays; the live one is not
// deleted until another is live.
// A world atlas (issue 1173): "Links on this map" puts a marker on the scene on the DM's screen that leads to another
// scene — the world's map to a region's, the region's to a town's — hidden from players until the DM shows it.
import { useState, type FormEvent } from 'react';
import { useShell } from '../context';
import { useTableState } from '../../table/TableState';
import { createScene, deleteScene, goLive, refusalWords, renameScene, type TableResult } from '../../table';
import type { SceneLink } from '@dragons-table/vtt-shared';
import { boardBus } from '../../board/bus';
import { useFollowLink } from '../../board/LinkOverlays';
import { menuSpot } from '../../place';
import { ConfirmDialog } from '../Dialogs';
import { BTN } from '../ui';

const btn = BTN;

export function ScenesPanel() {
  const { table, campaignId, onResult } = useShell();
  const { scene, viewScene, setNotice } = useTableState();
  const [name, setName] = useState('');
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
  // Asked in the page, never window.confirm (issue 1431): a browser that suppresses native dialogs left Delete doing nothing.
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const scenes = table.scenes ?? [];
  const first = scenes[0]?.id;
  // A refusal in words: the server's reason, or its code put in words (the review of 2026-10-10), never the bare code.
  const said = (r: TableResult) => { if (r.kind === 'refused') setNotice(refusalWords(r)); };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const r = await createScene(campaignId, name.trim());
    onResult(r);
    said(r);
    if (r.kind === 'ok') { setName(''); if (r.sceneId) viewScene(r.sceneId); }
  };

  return (
    <div className="space-y-3 p-2 text-sm" data-testid="scenes">
      <p className="text-xs text-stone-400">Each scene is its own map with everything on it. <em>View</em> shows it to you alone; <em>Go live</em> moves every player to it.</p>
      <ul className="divide-y divide-stone-800">
        {scenes.map((s) => {
          const here = scene?.id === s.id;
          return (
            <li key={s.id} className={`flex flex-wrap items-center gap-2 py-1.5 ${here ? 'rounded bg-stone-800/60 px-1' : ''}`} data-testid={`scene-${s.id}`} data-name={s.name}>
              {renaming?.id === s.id ? (
                <form className="flex items-center gap-1" onSubmit={async (e) => { e.preventDefault(); if (!renaming.name.trim()) return; const r = await renameScene(campaignId, s.id, renaming.name.trim()); onResult(r); said(r); setRenaming(null); }}>
                  <input aria-label={`New name for ${s.name}`} className="w-32 rounded bg-stone-800 px-1 py-0.5 text-xs" maxLength={60} value={renaming.name} onChange={(e) => setRenaming({ id: s.id, name: e.target.value })} />
                  <button type="submit" className={btn} disabled={!renaming.name.trim()}>Save</button>
                </form>
              ) : (
                <span className="font-medium">{s.name}</span>
              )}
              {s.live && <span className="rounded bg-emerald-800 px-1.5 text-xs text-emerald-50" data-testid="live-badge">live</span>}
              {here && <span className="text-xs text-stone-400">on your screen</span>}
              <span className="text-xs text-stone-500">{s.map ? s.map.name : 'empty grid'}</span>
              <span className="ml-auto flex gap-1">
                <button type="button" className={btn} disabled={here} aria-label={`View ${s.name}`} onClick={() => viewScene(s.id)}>View</button>
                <button type="button" className={btn} disabled={s.live} aria-label={`Put ${s.name} live`} onClick={async () => { const r = await goLive(campaignId, s.id); onResult(r); said(r); }}>Go live</button>
                <button type="button" className={btn} aria-label={`Rename ${s.name}`} onClick={() => setRenaming({ id: s.id, name: s.name })}>Rename</button>
                <button type="button" className={btn} disabled={s.live || s.id === first} aria-label={`Delete ${s.name}`}
                  title={s.id === first ? 'The first scene holds the roll log and stays.' : s.live ? 'The players are on this scene. Put another one live first.' : undefined}
                  onClick={() => setDeleting({ id: s.id, name: s.name })}>Delete</button>
              </span>
            </li>
          );
        })}
      </ul>
      <form className="flex items-center gap-1" onSubmit={add}>
        <label htmlFor="new-scene" className="text-xs text-stone-400">New scene</label>
        <input id="new-scene" className="w-40 rounded bg-stone-800 px-2 py-0.5 text-xs" placeholder="The crypt" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className={btn} disabled={!name.trim()}>Add</button>
      </form>
      <SceneLinks />
      {deleting && (
        <ConfirmDialog title={`Delete ${deleting.name} and everything on it?`} body={null} confirmLabel="Delete" destructive
          onClose={() => setDeleting(null)}
          onConfirm={async () => { const d = deleting; setDeleting(null); const r = await deleteScene(campaignId, d.id); onResult(r); said(r); }} />
      )}
    </div>
  );
}

/** The scene links on the map on the DM's screen (issue 1173): add one (it lands at the centre of the view), show or hide it, follow it, remove it. */
function SceneLinks() {
  const { table } = useShell();
  const { scene, store, act } = useTableState();
  const follow = useFollowLink();
  const others = (table.scenes ?? []).filter((s) => s.id !== scene?.id);
  const [to, setTo] = useState('');
  const [label, setLabel] = useState('');
  if (!scene || !others.length) return null;
  const target = others.some((s) => s.id === to) ? to : others[0]!.id;
  const links = Object.values((store.state.links ?? {}) as Record<string, SceneLink>);
  const nameOf = (id: string) => (table.scenes ?? []).find((s) => s.id === id)?.name ?? 'a scene that is gone';
  const add = (e: FormEvent) => {
    e.preventDefault();
    // The spot the board's menu chose ("Add a scene link here", qa-runs#1425), else the centre of the view.
    const at = menuSpot.take('link') ?? boardBus.viewCentre() ?? { x: 35, y: 35 };
    act({ kind: 'link.set', link: { id: crypto.randomUUID(), at: { x: Math.round(at.x), y: Math.round(at.y) }, label: (label.trim() || nameOf(target)).slice(0, 60), toScene: target, shown: false } });
    setLabel('');
  };
  return (
    <section className="space-y-2 border-t border-stone-800 pt-2" data-testid="scene-links" aria-label="Links on this map">
      <h3 className="vtt-section-head">Links on this map</h3>
      <p className="text-xs text-stone-400">A marker that leads to another scene: click it on the board to go there with the table, or open it for yourself. Players see a link only when you show it, and never where it leads.</p>
      {links.length > 0 && (
        <ul className="divide-y divide-stone-800">
          {links.map((l) => (
            <li key={l.id} className="flex flex-wrap items-center gap-2 py-1" data-testid="scene-link-row" data-label={l.label}>
              <span className="font-medium">{l.label}</span>
              <span className="text-xs text-stone-500">→ {nameOf(l.toScene)}</span>
              <span className="ml-auto flex gap-1">
                <button type="button" className={btn} aria-pressed={l.shown} aria-label={`${l.shown ? 'Hide' : 'Show'} ${l.label} ${l.shown ? 'from' : 'to'} players`} onClick={() => act({ kind: 'link.set', link: { ...l, shown: !l.shown } })}>{l.shown ? 'Shown' : 'Hidden'}</button>
                <button type="button" className={btn} aria-label={`Go there: ${l.label}`} onClick={() => void follow(l.toScene, true)}>Go there</button>
                <button type="button" className={btn} aria-label={`Remove ${l.label}`} onClick={() => act({ kind: 'link.remove', id: l.id })}>Remove</button>
              </span>
            </li>
          ))}
        </ul>
      )}
      <form className="flex flex-wrap items-center gap-1" onSubmit={add}>
        <label htmlFor="link-to" className="text-xs text-stone-400">Link to</label>
        <select id="link-to" className="rounded bg-stone-800 px-1 py-0.5 text-xs" value={target} onChange={(e) => setTo(e.target.value)}>
          {others.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input aria-label="Link label" className="w-32 rounded bg-stone-800 px-2 py-0.5 text-xs" placeholder={nameOf(target)} maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} />
        <button type="submit" className={btn}>Add link</button>
      </form>
    </section>
  );
}
